import { FuelItemSlug, getFuelItem } from '@/constants/fuel';
import fuelStaticContent from '@/constants/fuel_static_content.json';
import { ALL_DISTRICTS, ALL_DISTRICTS_FLAT } from '@/constants/location_districts';
import { ALL_STATES } from '@/constants/location_states';
import { toCleanSlug, toInternalDistrictSlug, toInternalStateSlug } from '@/lib/location_slug_mapper';

export const FUEL_BACKGROUND_URL =
  'https://static.inshorts.com/inshorts/images/v1/variants/jpg/m/2024/11_nov/6_wed/img_1730877469589_627.jpg';

const FUEL_ITEM_PATH_SUFFIX = '_prices';

export function decodeFuelItemParam(itemParam: string) {
  const decoded = decodeURIComponent(itemParam || '').trim();
  if (decoded.endsWith(FUEL_ITEM_PATH_SUFFIX)) {
    return decoded.slice(0, -FUEL_ITEM_PATH_SUFFIX.length);
  }
  return decoded;
}

export function toFuelItemParam(itemId: string) {
  return encodeURIComponent(`${itemId}${FUEL_ITEM_PATH_SUFFIX}`);
}

export function getValidatedFuelItem(itemParam: string) {
  const decodedItemId = decodeFuelItemParam(itemParam);
  const item = getFuelItem(decodedItemId);
  if (!item) {
    return null;
  }
  return item;
}

export function getValidatedFuelState(stateParam: string) {
  const stateInternalSlug = toInternalStateSlug(stateParam);
  if (!stateInternalSlug) {
    return null;
  }
  const state = ALL_STATES[stateInternalSlug];
  if (!state) {
    return null;
  }
  return {
    stateInternalSlug,
    stateCleanSlug: toCleanSlug(stateInternalSlug),
    state,
  };
}

export function getValidatedFuelDistrict(stateParam: string, districtParam: string) {
  const validatedState = getValidatedFuelState(stateParam);
  if (!validatedState) {
    return null;
  }
  const districtInternalSlug = toInternalDistrictSlug({
    stateInternalSlug: validatedState.stateInternalSlug,
    districtParam,
  });
  if (!districtInternalSlug) {
    return null;
  }
  const district = ALL_DISTRICTS_FLAT[districtInternalSlug];
  if (!district || !district.value.startsWith(`${validatedState.state.value}_`)) {
    return null;
  }
  return {
    ...validatedState,
    districtInternalSlug,
    districtCleanSlug: toCleanSlug(districtInternalSlug),
    district,
  };
}

export function listFuelStates() {
  return Object.entries(ALL_STATES).map(([stateInternalSlug, state]) => ({
    stateInternalSlug,
    stateCleanSlug: toCleanSlug(stateInternalSlug),
    state,
  }));
}

export function listFuelDistrictsByState(stateInternalSlug: string) {
  const state = ALL_STATES[stateInternalSlug];
  if (!state) {
    return [];
  }
  const districts = ALL_DISTRICTS[state.value] || {};
  return Object.entries(districts).map(([districtInternalSlug, district]) => ({
    districtInternalSlug,
    districtCleanSlug: toCleanSlug(districtInternalSlug),
    district,
  }));
}

export function getFuelItemPath(itemId: FuelItemSlug) {
  return `/fuel/${toFuelItemParam(itemId)}`;
}

export function getTodayDDMMYYYY() {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).formatToParts(new Date());
  const day = parts.find((part) => part.type === 'day')?.value || '01';
  const month = parts.find((part) => part.type === 'month')?.value || '01';
  const year = parts.find((part) => part.type === 'year')?.value || '1970';
  return `${day}/${month}/${year}`;
}

function fillFuelTemplate(template: string, values: Record<string, string>) {
  return Object.entries(values).reduce((acc, [key, value]) => {
    const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const anglePattern = new RegExp(`<${escapedKey}>`, 'gi');
    const squarePattern = new RegExp(`\\[${escapedKey}\\]`, 'gi');
    const curlyPattern = new RegExp(`\\{${escapedKey}\\}`, 'gi');
    return acc
      .replace(anglePattern, value)
      .replace(squarePattern, value)
      .replace(curlyPattern, value);
  }, template);
}

function sanitizeFuelTemplate(template: string) {
  return template.trim().replace(/^"+|"+$/g, '').replace(/"/g, '');
}

export function getFuelInsightParagraphs({
  itemId,
  tenant,
  stateName,
  districtName,
  currentPrice,
  changeDirection,
  changeRsLabel,
  changePercentLabel,
}: {
  itemId: string;
  tenant: string;
  stateName: string;
  districtName: string;
  currentPrice: string;
  changeDirection: string;
  changeRsLabel: string;
  changePercentLabel: string;
}) {
  const copyByFuel = fuelStaticContent[itemId as keyof typeof fuelStaticContent];
  if (!copyByFuel) {
    return [];
  }
  const normalizedTenant = String(tenant || 'hi').toLowerCase();
  const tenantCandidates = [
    normalizedTenant,
    normalizedTenant === 'mr' ? 'ma' : normalizedTenant === 'ma' ? 'mr' : '',
    'hi',
    'en',
  ].filter(Boolean);
  const rawTemplate = tenantCandidates
    .map((tenantKey) => copyByFuel[tenantKey as keyof typeof copyByFuel])
    .find(Boolean);
  if (!rawTemplate) {
    return [];
  }
  const resolvedTemplate = fillFuelTemplate(sanitizeFuelTemplate(rawTemplate), {
    'city/district name': districtName,
    'state name': stateName,
    price: currentPrice,
    price_change_in_rs: changeRsLabel,
    percentage_change: changePercentLabel,
    'increased/decreased': changeDirection,
    'ಹೆಚ್ಚಳ/ಕಡಿಮೆಯಾಗಿದೆ': changeDirection,
    'ನಗರ/ಜಿಲ್ಲೆಯ ಹೆಸರು': districtName,
    'ರಾಜ್ಯದ ಹೆಸರು': stateName,
  });
  return resolvedTemplate
    .split(/\n\s*\n/g)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
}

export function formatFuelDateLabel(dateValue: string) {
  const [day, month, year] = dateValue.split('/');
  if (!day || !month || !year) {
    return dateValue;
  }
  const parsed = new Date(`${year}-${month}-${day}T00:00:00+05:30`);
  if (Number.isNaN(parsed.getTime())) {
    return dateValue;
  }
  return new Intl.DateTimeFormat('hi-IN', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'Asia/Kolkata',
  }).format(parsed);
}
